import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import {
  RelationshipManagerService,
  TransferRMPayload,
} from '@core/services/relationship-manager.service';
import { Store } from '@ngxs/store';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { ButtonDirective, SpinDirective } from '@shared/directives/button.directive';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Observable, Subscription } from 'rxjs';

interface RMOption {
  id: string;
  first_name: string;
  last_name: string;
}

const CATEGORY_OPTIONS = [
  { key: 'individual', value: 'Individual' },
  { key: 'joint', value: 'Joint' },
  { key: 'corporate', value: 'Corporate' },
];

@Component({
  selector: 'app-transfer-accounts-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BaseSelectDirective,
    ButtonDirective,
    SpinDirective,
    SlideInRightModalComponent,
  ],
  templateUrl: './transfer-accounts-modal.component.html',
  styleUrl: './transfer-accounts-modal.component.scss',
})
export class TransferAccountsModalComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private relationshipManagerService = inject(RelationshipManagerService);
  private toast = inject(NotificationFacade);
  private change = inject(ChangeDetectorRef);
  private subs: Subscription[] = [];

  currentRMId = '';
  currentRMName = '';
  otherRMs: RMOption[] = [];
  categoryOptions = CATEGORY_OPTIONS;
  accounts: any[] = [];
  isFetchingAccounts = false;
  isSubmitting = false;
  accountTypeFilter = '';
  accountSearchQuery = '';
  private accountsLoadedForRMId: string | null = null;

  RMList$: Observable<any> = this.store.select(
    TableSelectors.getTableData('relationshipManagers')
  );

  transferForm = this.fb.nonNullable.group({
    new_rm_id: ['', Validators.required],
    scope: ['', Validators.required],
    category: [''],
    selected_account_ids: [[] as string[]],
  });

  ngOnInit(): void {
    this.currentRMName = this.route.snapshot.queryParams['relationship_manager'] || '';

    const rmSub = this.RMList$.subscribe((list) => {
      if (Array.isArray(list)) {
        const rmIds: Record<string, string> = {};
        list.forEach((rm: any) => {
          const first = rm.first_name ?? '';
          const last = rm.last_name ?? '';
          const value =
            first && last
              ? `${first.replace(' ', '')} ${last.replace(' ', '')}`
              : first || last || '';
          rmIds[value.replace(' ', '')] = rm.id;
        });

        this.currentRMId = rmIds[this.currentRMName.replace(' ', '')] || '';
        this.otherRMs = list.filter((rm: any) => rm.id !== this.currentRMId);
        this.change.detectChanges();
      }
    });
    this.subs.push(rmSub);

    const scopeSub = this.transferForm.controls.scope.valueChanges.subscribe(
      (scope) => {
        if (scope === 'selected' && this.accountsLoadedForRMId !== this.currentRMId) {
          this.loadAccountsForCurrentRM();
        }
      }
    );
    this.subs.push(scopeSub);
  }

  get filteredAccounts(): any[] {
    return this.accounts.filter((account) => {
      const matchesType =
        !this.accountTypeFilter || account.account_type === this.accountTypeFilter;
      const query = this.accountSearchQuery.trim().toLowerCase();
      const matchesSearch =
        !query ||
        this.getAccountLabel(account).toLowerCase().includes(query) ||
        (account.mango_account_number || '').toLowerCase().includes(query);
      return matchesType && matchesSearch;
    });
  }

  loadAccountsForCurrentRM() {
    if (!this.currentRMId) return;

    this.isFetchingAccounts = true;
    const sub = this.relationshipManagerService
      .getAllAccounts(
        undefined,
        `relationship_manager_id=${this.currentRMId}&skip=0&take=1000`
      )
      .subscribe({
        next: (response) => {
          this.accounts = response.data?.list || [];
          this.accountsLoadedForRMId = this.currentRMId;
          this.isFetchingAccounts = false;
          this.change.detectChanges();
        },
        error: (err) => {
          this.isFetchingAccounts = false;
          this.toast.error(err);
          this.change.detectChanges();
        },
      });
    this.subs.push(sub);
  }

  getAccountLabel(account: any): string {
    if (account.account_type === 'corporate') {
      return account.corporate_kyc?.name || account.mango_account_number;
    }
    const first = account.user_profile?.first_name || '';
    const last = account.user_profile?.last_name || '';
    return `${first} ${last}`.trim() || account.mango_account_number;
  }

  toggleAccountSelection(accountId: string, checked: boolean) {
    const current = this.transferForm.controls.selected_account_ids.value;
    const updated = checked
      ? [...current, accountId]
      : current.filter((id) => id !== accountId);
    this.transferForm.controls.selected_account_ids.setValue(updated);
  }

  isAccountSelected(accountId: string): boolean {
    return this.transferForm.controls.selected_account_ids.value.includes(
      accountId
    );
  }

  get isFormValid(): boolean {
    const { new_rm_id, scope, category, selected_account_ids } =
      this.transferForm.getRawValue();
    if (!new_rm_id || !scope) return false;
    if (scope === 'category' && !category) return false;
    if (scope === 'selected' && selected_account_ids.length === 0) return false;
    return true;
  }

  submit() {
    if (!this.isFormValid || !this.currentRMId) return;

    const { new_rm_id, scope, category, selected_account_ids } =
      this.transferForm.getRawValue();

    const payload: TransferRMPayload = {
      from_relationship_manager_id: this.currentRMId,
      to_relationship_manager_id: new_rm_id,
      transfer_type: scope as TransferRMPayload['transfer_type'],
      ...(scope === 'category' ? { account_type: category } : {}),
      ...(scope === 'selected' ? { account_ids: selected_account_ids } : {}),
    };

    this.isSubmitting = true;
    const sub = this.relationshipManagerService
      .transferRelationshipManager(payload)
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.toast.success('Accounts transferred successfully');
          this.refreshAccountsList();
          this.goBack();
        },
        error: (err) => {
          this.isSubmitting = false;
          this.toast.error(err);
          this.change.detectChanges();
        },
      });
    this.subs.push(sub);
  }

  private refreshAccountsList() {
    this.store.dispatch(new TableActions.GetAccountsRMMetadata());

    const params = this.route.snapshot.queryParams;
    let query = '';
    if (params['relationship_manager']) {
      query += `relationship_manager_id=${this.currentRMId}&`;
    }
    if (params['account_type']) {
      query += `account_type=${params['account_type']}&`;
    }
    if (params['kyc_status']) {
      query += `kyc_status=${params['kyc_status']}&`;
    }
    if (params['rm_status']) {
      query += `relationship_manager=${params['rm_status']}&`;
    }
    if (params['search']) {
      query += `search_text=${params['search']}&`;
    }

    const rawPage = params['page'] ? parseInt(params['page']) : 1;
    const page = rawPage > 1 ? rawPage - 1 : 0;
    query += `skip=${page * 10}&take=10`;

    this.store.dispatch(new TableActions.FilterAccountRMList({ filter: query }));
  }

  goBack() {
    history.back();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
